import {useQuery, useSuspenseQuery} from '@apollo/client'
import {
  GetCategoriesAndProductsWeb_CachedQuery,
  GetCategoriesAndProductsWeb_CachedQueryVariables
} from '@data/__generated__/types.main'
import {categoriesForRestaurantQuery} from '@data/queries/categories/categoriesForWebsite.main'
import isServerSide from '@helpers/misc/isServerSide'

import {useDefaultMenuId} from './getDefaultMenu.main'

export interface Props {
  websiteId: string
  menuId?: string
  format?: string
  omit?: boolean
}

export default function useCategoriesAndProducts({
  websiteId,
  menuId,
  format = 'webp',
  omit = false
}: Props) {
  const defaultMenuId = useDefaultMenuId()

  const defaultMenu = useSuspenseQuery<
    GetCategoriesAndProductsWeb_CachedQuery,
    GetCategoriesAndProductsWeb_CachedQueryVariables
  >(categoriesForRestaurantQuery, {
    variables: {websiteId, menuId: defaultMenuId, format},
    fetchPolicy: 'cache-first',
    skip: omit
  })

  /**
   * We do one cached query for the default menu and one partial query for the current menu.
   * In this way, we can render the default menu while the current menu is loading.
   * By doing this, there should be no loading time between the default menu and the current menu.
   */
  const result = useQuery<
    GetCategoriesAndProductsWeb_CachedQuery,
    GetCategoriesAndProductsWeb_CachedQueryVariables
  >(categoriesForRestaurantQuery, {
    variables: {websiteId, menuId, format},
    returnPartialData: false,
    skip: omit || isServerSide() || !menuId
  })

  return result?.data?.categories ? result.data : defaultMenu.data
}

